import React from 'react';
import { Box, Paper, Typography, Skeleton, useTheme } from '@mui/material';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { PerformanceHistoryItem } from '../../hooks/useStatistics';
import { BenchmarkPoint } from '../../hooks/useStatisticsPanels';
import { accentColors, pnlColors } from '../../design/theme';
import { formatSignedCurrency, formatShortDate, formatCompactCurrency } from '../../design/format';

interface StatisticsPerformanceChartProps {
  history: PerformanceHistoryItem[];
  benchmark: BenchmarkPoint[] | null;
  benchmarkFailed: boolean;
  loading: boolean;
  deposits: number | null;
}

interface ChartRow {
  date: string;
  cumulative: number;
  cumulativePct?: number;
  spy?: number;
}

const SPY_STROKE = accentColors.spy;
const CHART_HEIGHT = 380;

const formatSignedPercent = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

// Value color is sign-only (green/red); the label carries the series color.
const signColor = (value: number) =>
  value > 0 ? pnlColors.gain : value < 0 ? pnlColors.loss : undefined;

const TooltipRow: React.FC<{ label: string; labelColor: string; value: number; formatted: string }> = ({
  label,
  labelColor,
  value,
  formatted,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body2" sx={{ color: labelColor }}>{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, color: signColor(value) }}>
      {formatted}
    </Typography>
  </Box>
);

const ChartTooltip: React.FC<TooltipProps<number, string> & { showPct: boolean; showSpy: boolean }> = ({
  active,
  payload,
  showPct,
  showSpy,
}) => {
  const theme = useTheme();
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload as ChartRow;
  return (
    <Paper
      elevation={4}
      sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
        {new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TooltipRow
          label="Cumulative P&L"
          labelColor={theme.palette.primary.main}
          value={row.cumulative}
          formatted={formatSignedCurrency(row.cumulative)}
        />
        {showPct && row.cumulativePct !== undefined && (
          <TooltipRow
            label="Cumulative P&L %"
            labelColor={theme.palette.primary.main}
            value={row.cumulativePct}
            formatted={formatSignedPercent(row.cumulativePct)}
          />
        )}
        {showSpy && row.spy !== undefined && (
          <TooltipRow
            label="SPY %"
            labelColor={SPY_STROKE}
            value={row.spy}
            formatted={formatSignedPercent(row.spy)}
          />
        )}
      </Box>
    </Paper>
  );
};

const StatisticsPerformanceChart: React.FC<StatisticsPerformanceChartProps> = ({
  history,
  benchmark,
  benchmarkFailed,
  loading,
  deposits,
}) => {
  const theme = useTheme();

  if (loading) {
    return <Skeleton variant="rounded" height={CHART_HEIGHT} />;
  }

  if (!history || history.length === 0) {
    return (
      <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No data available for selected period
        </Typography>
      </Box>
    );
  }

  const hasBenchmark = !benchmarkFailed && !!benchmark && benchmark.length > 0;
  // ÷-Deposits percent (house convention); unavailable deposits just hide the % line.
  const hasPct = deposits !== null && deposits > 0;
  const spyByDate = new Map(benchmark?.map((b) => [b.date, b.close]));
  const firstClose = benchmark?.[0]?.close;

  const rows: ChartRow[] = history.map((h) => ({
    date: h.date,
    cumulative: h.cumulative_pnl,
    cumulativePct: hasPct ? (h.cumulative_pnl / deposits) * 100 : undefined,
    spy:
      hasBenchmark && spyByDate.has(h.date) && firstClose
        ? (spyByDate.get(h.date)! / firstClose - 1) * 100
        : undefined,
  }));

  const rangeLabel = `${formatShortDate(history[0].date)} to ${formatShortDate(history[history.length - 1].date)}`;
  const description = `Performance chart showing a single cumulative profit and loss line${
    hasPct ? ' as a percent of deposits' : ' in dollars'
  }${hasBenchmark ? ', with an SPY percentage-return overlay,' : ''} from ${rangeLabel}.`;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ width: '100%', height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis
              dataKey="date"
              tickFormatter={formatShortDate}
              tickLine={false}
              axisLine={false}
              fontSize={12}
              stroke={theme.palette.text.secondary}
            />
            {/* One P&L line only — $ and % are the same curve (÷ deposits), so
                plotting both just duplicates it. % when deposits are known
                (shares the % axis with SPY); $ fallback otherwise. */}
            {hasPct ? (
              <YAxis
                yAxisId="pct"
                tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                tickLine={false}
                axisLine={false}
                fontSize={12}
                width={56}
                stroke={theme.palette.text.secondary}
              />
            ) : (
              <>
                <YAxis
                  yAxisId="pnl"
                  tickFormatter={formatCompactCurrency}
                  tickLine={false}
                  axisLine={false}
                  fontSize={12}
                  width={60}
                  stroke={theme.palette.text.secondary}
                />
                {hasBenchmark && (
                  <YAxis
                    yAxisId="pct"
                    orientation="right"
                    tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                    tickLine={false}
                    axisLine={false}
                    fontSize={12}
                    width={56}
                    stroke={theme.palette.text.secondary}
                  />
                )}
              </>
            )}
            <Tooltip content={<ChartTooltip showPct={hasPct} showSpy={hasBenchmark} />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Legend />
            {hasPct ? (
              <Line
                yAxisId="pct"
                type="monotone"
                dataKey="cumulativePct"
                name="Cumulative P&L %"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={false}
              />
            ) : (
              <Line
                yAxisId="pnl"
                type="monotone"
                dataKey="cumulative"
                name="Cumulative P&L"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={false}
              />
            )}
            {hasBenchmark && (
              <Line
                yAxisId="pct"
                type="monotone"
                dataKey="spy"
                name="SPY %"
                stroke={SPY_STROKE}
                strokeDasharray="6 3"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {!hasBenchmark && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          SPY overlay unavailable
        </Typography>
      )}

      <Box
        role="img"
        aria-label={description}
        sx={{ position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
      >
        {description}
      </Box>
    </Box>
  );
};

export default StatisticsPerformanceChart;
